import { useState } from 'react';
import { Button, Popover, ScrollArea, Skeleton, Text, UnstyledButton, VisuallyHidden } from '@mantine/core';
import { IconBell, IconSettings } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';

import { TabBadge } from '@/components/ri/TabBadge';
import { useApprovalBadgeCount, useApprovals, type ApprovalItem } from '@/hooks/useApprovals';
import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotifications,
  useUnreadNotificationCount,
  type InAppNotification,
} from '@/hooks/useNotifications';
import { formatDollars } from '@/lib/format';
import { formatRelativeShort } from '@/lib/relativeTime';

import './NotificationBell.css';

const APPROVAL_PREVIEW_COUNT = 3;

interface NotificationBellProps {
  showApprovals?: boolean;
}

export function NotificationBell({ showApprovals = false }: NotificationBellProps) {
  const [opened, setOpened] = useState(false);
  const navigate = useNavigate();

  const { data: unread } = useUnreadNotificationCount();
  const unreadCount = unread?.count ?? 0;
  const feed = useNotifications(opened);
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();

  const { data: badge } = useApprovalBadgeCount(showApprovals);
  const pendingCount = showApprovals ? (badge?.count ?? 0) : 0;
  const approvals = useApprovals(
    { status: 'Pending', assignedToMe: true, page: 1, pageSize: APPROVAL_PREVIEW_COUNT },
    opened && pendingCount > 0,
  );
  const approvalItems = approvals.data?.items ?? [];
  const items = feed.data?.items ?? [];
  const hasUnread = unreadCount > 0 || items.some((item) => !item.readAt);

  const goTo = (path: string) => {
    setOpened(false);
    navigate(path);
  };

  const openNotification = (item: InAppNotification) => {
    if (!item.readAt) markRead.mutate(item.id);
    if (item.link.startsWith('/')) goTo(item.link);
    else setOpened(false);
  };

  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      position="bottom-end"
      width={360}
      withinPortal
      shadow="lg"
      radius={12}
      offset={6}
    >
      <Popover.Target>
        <UnstyledButton
          className="notif-bell-trigger"
          onClick={() => setOpened((o) => !o)}
          aria-label={unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications'}
        >
          <span style={{ position: 'relative', display: 'inline-flex' }}>
            <IconBell size={18} />
            {unreadCount > 0 && <TabBadge tone="warning">{unreadCount > 9 ? '9+' : unreadCount}</TabBadge>}
          </span>
        </UnstyledButton>
      </Popover.Target>

      <Popover.Dropdown p={0} className="notif-bell-dropdown">
        <div className="notif-bell-header">
          <span>Notifications</span>
          <Button
            variant="subtle"
            size="compact-xs"
            disabled={!hasUnread || markAllRead.isPending}
            onClick={() => markAllRead.mutate()}
          >
            Mark all read
          </Button>
        </div>

        {pendingCount > 0 && (
          <section className="notif-bell-approvals" aria-label="Waiting on your approval">
            <div className="notif-bell-section-label">Waiting on your approval · {pendingCount}</div>
            {approvals.isLoading ? (
              <Skeleton height={40} radius={8} m={6} />
            ) : approvals.isError ? (
              <Text className="notif-bell-note">Couldn't load your pending approvals.</Text>
            ) : (
              approvalItems.map((item) => (
                <ApprovalRow key={item.id} item={item} onSelect={(id) => goTo(`/approvals?focus=${id}`)} />
              ))
            )}
            <UnstyledButton className="notif-bell-link" onClick={() => goTo('/approvals')}>
              {pendingCount > approvalItems.length && approvalItems.length > 0
                ? `+${pendingCount - approvalItems.length} more · View all approvals →`
                : 'View all approvals →'}
            </UnstyledButton>
          </section>
        )}

        {feed.isLoading ? (
          <div style={{ padding: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <Skeleton height={52} radius={8} />
            <Skeleton height={52} radius={8} />
          </div>
        ) : feed.isError ? (
          <Text className="notif-bell-note">Couldn't load your notifications. Try again in a moment.</Text>
        ) : items.length === 0 ? (
          <div className="notif-bell-empty">
            <strong>You're all caught up</strong>
            <span>We'll let you know here when customers view, accept, or pay your quotes.</span>
          </div>
        ) : (
          <ScrollArea.Autosize mah="min(420px, 60vh)" type="auto">
            <ul className="notif-bell-list" aria-label="Recent notifications">
              {items.map((item) => (
                <li key={item.id}>
                  <NotificationRow item={item} onSelect={openNotification} />
                </li>
              ))}
            </ul>
          </ScrollArea.Autosize>
        )}

        <div className="notif-bell-footer">
          <Button
            variant="subtle"
            size="sm"
            leftSection={<IconSettings size={16} />}
            onClick={() => goTo('/settings?tab=notifications')}
          >
            Notification settings
          </Button>
        </div>
      </Popover.Dropdown>
    </Popover>
  );
}

function NotificationRow({ item, onSelect }: { item: InAppNotification; onSelect: (item: InAppNotification) => void }) {
  const unread = !item.readAt;
  return (
    <UnstyledButton className="notif-bell-row" data-unread={unread || undefined} onClick={() => onSelect(item)}>
      <span className="notif-bell-dot" aria-hidden />
      {unread && <VisuallyHidden>Unread: </VisuallyHidden>}
      <span className="notif-bell-row-body">
        <span className="notif-bell-row-top">
          <span className="notif-bell-title">{item.title}</span>
          <Text component="span" size="xs" c="dimmed" className="notif-bell-time">
            {formatRelativeShort(item.createdAt)}
          </Text>
        </span>
        <span className="notif-bell-text">{item.body}</span>
      </span>
    </UnstyledButton>
  );
}

function ApprovalRow({ item, onSelect }: { item: ApprovalItem; onSelect: (id: string) => void }) {
  return (
    <UnstyledButton className="notif-bell-row" onClick={() => onSelect(item.id)}>
      <span className="notif-bell-row-body">
        <span className="notif-bell-row-top">
          <span className="notif-bell-title">
            {item.quoteNumber} — {item.accountName}
          </span>
          <Text component="span" size="xs" c="dimmed" className="notif-bell-time">
            {formatRelativeShort(item.requestedAt)}
          </Text>
        </span>
        <span className="notif-bell-text">
          {item.requestedByName} • {formatDollars(item.quoteTotal)}
        </span>
      </span>
    </UnstyledButton>
  );
}
